// Content Management System for itj.dev - DevOps Engineer with Full-Stack Capabilities
import { BRAND } from '../constants/brand';

export interface ContentData {
  navigation: {
    home: string;
    technologies: string;
    services: string;
    about: string;
    contact: string;
  };
  hero: {
    title: {
      line1: string;
      line2: string;
      line3: string;
    };
    description: string;
    introduction: string;
    tagline: string;
    stats: {
      projects: { number: number; label: string; value: string };
      years: { number: number; label: string; value: string };
      clients: { number: number; label: string; value: string };
      support: { text: string; label: string; value: string };
    };
    technologies: {
      title: string;
      subtitle: string;
    };
    cta: {
      button: string;
      badge: string;
    };
    terminalTechnologies: Array<{
      name: string;
      command: string;
      code: string;
      output: string;
      color: string;
    }>;
  };
  servicesPreview: {
    title: string;
    subtitle: string;
    items: Array<{
      title: string;
      description: string;
      icon: string;
    }>;
    ctaText: string;
    ctaUrl: string;
  };
  testimonials: {
    title: string;
    subtitle: string;
    items: Array<{
      id: number;
      name: string;
      position: string;
      company: string;
      content: string;
      rating: number;
      avatar?: string;
    }>;
  };
  portfolio: {
    title: string;
    subtitle: string;
    items: Array<{
      id: number;
      title: string;
      description: string;
      category: string;
      technologies: string[];
      image?: string;
      liveUrl?: string;
      githubUrl?: string;
      featured: boolean;
    }>;
  };
  githubProjects: {
    title: string;
    subtitle: string;
    username: string;
    featured: string[]; // Repository names to feature
  };
  services: {
    title: string;
    subtitle: string;
    items: Array<{
      id: number;
      title: string;
      description: string;
      technologies: string[];
      features: string[];
      highlighted?: boolean;
    }>;
    cta: {
      title: string;
      subtitle: string;
      button: string;
      badge: string;
    };
  };
  technologies: {
    title: string;
    subtitle: string;
    categories: Array<{
      title: string;
      color: string;
      technologies: Array<{
        name: string;
        description: string;
      }>;
    }>;
    cta: {
      badge: string;
    };
  };
  about: {
    title: string;
    subtitle: string;
    description: string;
    founderTitle: string;
    subtitles: {
      journey: string;
      values: string;
    };
    sectionTitles: {
      journey: string;
      values: string;
      skills: string;
      cta: string;
    };
    journey: Array<{
      period: string;
      title: string;
      description: string;
      color: string;
    }>;
    values: Array<{
      title: string;
      description: string;
      icon: string;
    }>;
    skills: Array<{
      skill: string;
    }>;
    cta: {
      title: string;
      subtitle: string;
      button: string;
    };
  };
  contact: {
    title: {
      line1: string;
      line2: string;
      line3: string;
    };
    description: string;
    info: {
      email_label: string;
      response_label: string;
      email: string;
      responseTime: string;
      location_label: string;
      location: string;
    };
    social_title: string;
    faq_title: string;
    faq_subtitle: string;
    form: {
      nameLabel: string;
      emailLabel: string;
      serviceLabel: string;
      messageLabel: string;
      submitButton: string;
      namePlaceholder: string;
      emailPlaceholder: string;
      messagePlaceholder: string;
    };
    social: {
      github: string;
      linkedin: string;
    };
    faq: Array<{
      question: string;
      answer: string;
    }>;
  };
  meta: {
    title: string;
    description: string;
    keywords: string;
    ogTitle: string;
    ogDescription: string;
    twitterTitle: string;
    twitterDescription: string;
    language: string;
    locale: string;
  };
}

// Polish Content (Default)
export const contentPL: ContentData = {
  navigation: {
    home: "Strona Główna",
    technologies: "Technologie",
    services: "Usługi",
    about: "O Mnie",
    contact: "Kontakt"
  },
  hero: {
    title: {
      line1: "Cloud.",
      line2: "Automate.",
      line3: "Scale."
    },
    description: "Senior DevOps & MLOps Engineer z 14+ lat doświadczenia. Projektuję infrastrukturę chmurową (Azure/AWS), wdrażam modele ML w produkcji i automatyzuję wszystko co się da. Od Kubernetes po integrację AI - dostarczam rozwiązania klasy enterprise.",
    introduction: "Cześć, nazywam się Jakub - specjalizuję się w DevOps & MLOps",
    tagline: "Infrastruktura Cloud • MLOps & AI Integration • Kubernetes • Automatyzacja CI/CD",
    stats: {
      projects: { number: 40, label: "Projektów Infrastrukturalnych", value: "40+" },
      years: { number: 14, label: "Lat Doświadczenia", value: "14+" },
      clients: { number: 15, label: "Migracji do Chmury", value: "15+" },
      support: { text: "100+", label: "Zautomatyzowanych Procesów", value: "100+" }
    },
    technologies: {
      title: "Technologie",
      subtitle: "Narzędzia które opanowałem"
    },
    cta: {
      button: "Rozpocznij Projekt",
      badge: "Gotowy na Rozwój?"
    },
    terminalTechnologies: [
      {
        name: "Go/Golang",
        command: "go run main.go",
        code: `package main\n\nimport "fmt"\n\nfunc main() {\n    fmt.Println("Hello, DevOps!")\n}`,
        output: "Hello, DevOps!",
        color: "from-blue-500 to-cyan-500"
      },
      {
        name: "Kubernetes",
        command: "kubectl apply -f deployment.yaml",
        code: `apiVersion: apps/v1\nkind: Deployment\nmetadata:\n  name: hello-app\nspec:\n  replicas: 3`,
        output: "deployment.apps/hello-app created\nservice/hello-app created",
        color: "from-purple-500 to-pink-500"
      },
      {
        name: "Docker",
        command: "docker run hello-world",
        code: `FROM node:18-alpine\nWORKDIR /app\nCOPY . .\nRUN npm install\nEXPOSE 3000\nCMD ["npm", "start"]`,
        output: "Hello from Docker!\nThis message shows that your installation appears to be working correctly.",
        color: "from-blue-600 to-indigo-600"
      },
      {
        name: "React",
        command: "npm start",
        code: `function App() {\n  return (\n    <div className="App">\n      <h1>Hello React!</h1>\n    </div>\n  );\n}`,
        output: "Kompilacja zakończona sukcesem!\nLocal: http://localhost:3000\nWebpack skompilowany bez błędów",
        color: "from-cyan-500 to-blue-500"
      },
      {
        name: "Ansible",
        command: "ansible-playbook deploy.yml",
        code: `---\n- name: Deploy Application\n  hosts: webservers\n  tasks:\n    - name: Install nginx\n      apt:\n        name: nginx\n        state: present\n    - name: Start nginx\n      service:\n        name: nginx\n        state: started`,
        output: "PLAY [Deploy Application] ****\n\nTASK [Install nginx] ****\nok: [web1]\nok: [web2]\n\nTASK [Start nginx] ****\nchanged: [web1]\nchanged: [web2]\n\nPLAY RECAP ****\nweb1: ok=2 changed=1\nweb2: ok=2 changed=1",
        color: "from-red-500 to-orange-500"
      },
      {
        name: "Terraform",
        command: "terraform apply",
        code: `resource "aws_instance" "web" {\n  ami           = "ami-0c55b159cbfafe1d0"\n  instance_type = "t3.micro"\n  \n  tags = {\n    Name = "HelloWorld"\n  }\n}`,
        output: "Wdrożenie zakończone! Zasoby: 1 dodany, 0 zmienionych, 0 usuniętych.\nWyjście:\ninstance_ip = 54.123.45.67",
        color: "from-purple-600 to-pink-600"
      },
      {
        name: "PostgreSQL",
        command: "psql -c \"SELECT 'Hello Database!'\"",
        code: `CREATE TABLE users (\n  id SERIAL PRIMARY KEY,\n  name VARCHAR(100),\n  email VARCHAR(100)\n);\n\nINSERT INTO users (name, email) VALUES\n('DevOps Engineer', 'hello@itj.dev');`,
        output: "     ?column?\n------------------\n Hello Database!\n(1 wiersz)\n\nINSERT 0 1",
        color: "from-blue-700 to-indigo-700"
      }
    ]
  },
  servicesPreview: {
    title: "DevOps • MLOps • Architektura Chmury",
    subtitle: "Infrastruktura klasy enterprise i automatyzacja na każdym poziomie",
    items: [
      {
        title: "Infrastruktura Cloud i DevOps",
        description: "Azure/AWS, Kubernetes, Terraform, CI/CD - skalowalna infrastruktura produkcyjna z automatyzacją i monitoringiem",
        icon: "cloud"
      },
      {
        title: "MLOps i Integracja AI",
        description: "Wdrażanie modeli ML w produkcji, integracja ChatGPT/Claude/Copilot, automatyzacja procesów z AI",
        icon: "ai"
      },
      {
        title: "Aplikacje Web i Mobile",
        description: "Aplikacje React i React Native z nowoczesnym stosem technologicznym - od MVP po rozwiązania enterprise",
        icon: "mobile"
      }
    ],
    ctaText: "Zobacz Szczegóły Usług",
    ctaUrl: "/uslugi"
  },
  testimonials: {
    title: "Referencje Projektowe",
    subtitle: "Opinie od menedżerów technicznych o realizowanych projektach systemowych",
    items: [
      {
        id: 1,
        name: "Michał Kowalski",
        position: "CTO",
        company: "FinTech Scale-up",
        content: "Jakub zaprojektował kompletną architekturę naszej platformy - od serwera w Go po infrastrukturę Kubernetes. Jego umiejętność łączenia programowania z ekspertyzą DevOps pozwoliła nam obsłużyć 10x większy ruch bez problemów.",
        rating: 5
      },
      {
        id: 2,
        name: "Anna Nowak",
        position: "Engineering Manager",
        company: "E-commerce Enterprise",
        content: "Modernizacja infrastruktury z migracją do chmury. Jakub prowadził zespół 6 osób, wdrożył automatyzację CI/CD i zredukował czas wdrożeń z tygodni do minut. Praktyczne podejście i konkretne rezultaty.",
        rating: 5
      },
      {
        id: 3,
        name: "Piotr Wiśniewski",
        position: "Head of Platform",
        company: "International SaaS",
        content: "Potrzebowaliśmy eksperta który rozumie zarówno kod jak i infrastrukturę. Jakub zaprojektował mikrousługi w Go, wdrożył service mesh i zaprojektował monitoring. Efektywność zespołu znacznie wzrosła.",
        rating: 5
      }
    ]
  },
  portfolio: {
    title: "Projekty Infrastrukturalne i MLOps",
    subtitle: "Realizacje klasy enterprise: migracje do chmury, Kubernetes, automatyzacja CI/CD i wdrożenia MLOps",
    items: [
      {
        id: 1,
        title: "Infrastruktura E-commerce - Azure + Kubernetes",
        description: "Kompletna infrastruktura dla międzynarodowej platformy e-commerce. Migracja do Azure, klastry AKS z automatycznym skalowaniem, wdrożenia bez przestojów. Redukcja kosztów o 60%.",
        category: "Cloud + DevOps",
        technologies: ["Azure", "Kubernetes", "Terraform", "Helm", "Prometheus", "Grafana"],
        featured: true
      },
      {
        id: 2,
        title: "Proces MLOps - Platforma FinTech",
        description: "Platforma MLOps dla systemu scoringowego. Automatyczne trenowanie, walidacja i wdrażanie modeli ML. Magazyn cech, śledzenie eksperymentów, testy A/B w produkcji.",
        category: "MLOps + DevOps",
        technologies: ["Azure ML", "Kubeflow", "MLflow", "Kubernetes", "Python", "Terraform"],
        featured: true
      },
      {
        id: 3,
        title: "CI/CD Enterprise - Platforma Jakości Kodu",
        description: "Architektura procesów CI/CD dla platformy jakości kodu. Automatyczne testowanie, skanowanie bezpieczeństwa, wdrożenia wielośrodowiskowe. Integracja z ServiceNow.",
        category: "DevOps + Automatyzacja",
        technologies: ["Jenkins", "GitLab CI", "Azure DevOps", "Docker", "Kubernetes", "Checkov"],
        featured: true
      },
      {
        id: 4,
        title: "Infrastruktura IoT - Kubernetes + Monitoring",
        description: "Rozproszona infrastruktura dla platformy IoT obsługującej miliony urządzeń. Kubernetes lokalny, kolejki komunikatów, monitoring i alerty w czasie rzeczywistym.",
        category: "Infrastruktura + SRE",
        technologies: ["Kubernetes", "NATS", "InfluxDB", "Prometheus", "Grafana", "Terraform"],
        featured: true
      },
      {
        id: 5,
        title: "Migracja do Chmury - Branża Lotnicza",
        description: "Migracja infrastruktury o krytycznym znaczeniu dla branży lotniczej (zgodność FAA). Architektura chmury hybrydowej, migracja bez przestojów, odtwarzanie awaryjne.",
        category: "Migracja do Chmury",
        technologies: ["Azure", "AWS", "Terraform", "Ansible", "Docker", "Kubernetes"],
        featured: true
      },
      {
        id: 6,
        title: "Integracja AI - Automatyzacja Enterprise",
        description: "Wdrożenie GitHub Copilot i Claude AI w procesach programistycznych. Automatyzacja przeglądu kodu, dokumentacji i testowania. Integracja z istniejącym CI/CD.",
        category: "Integracja AI",
        technologies: ["GitHub Copilot", "Claude API", "Python", "Azure DevOps", "Docker"],
        featured: false
      }
    ]
  },
  githubProjects: {
    title: "Open Source Contributions",
    subtitle: "Wierzymy w wartość kodu otwartego - i staramy się kultywować tę ideę.",
    username: "truskul",
    featured: ["go-microservices-kit", "kubernetes-deployment-tools", "laravel-devops-helpers"]
  },
  services: {
    title: "DevOps • MLOps • Architektura Chmury",
    subtitle: "Infrastruktura klasy enterprise, automatyzacja i wdrażanie AI w produkcji",
    items: [
      {
        id: 1,
        title: "Infrastruktura Cloud i DevOps",
        description: "Projektowanie i wdrażanie infrastruktury chmurowej na Azure/AWS. Kubernetes, Infrastruktura jako Kod, monitoring i wysoka dostępność. Migracje do chmury z minimalizacją przestojów.",
        technologies: ["Azure", "AWS", "Kubernetes", "Terraform", "Helm", "Prometheus"],
        features: [
          "Architektura chmurowa Azure/AWS/GCP",
          "Klastry Kubernetes (AKS, EKS) produkcyjne",
          "Infrastruktura jako Kod z Terraform",
          "Monitoring, alertowanie i obserwowalność",
          "Odtwarzanie awaryjne i wysoka dostępność",
          "Optymalizacja kosztów i FinOps"
        ],
        highlighted: true
      },
      {
        id: 2,
        title: "MLOps i Infrastruktura ML",
        description: "Infrastruktura dla obciążeń Machine Learning. Procesy MLOps, wdrażanie modeli w produkcji, monitoring wydajności i automatyczne ponowne trenowanie.",
        technologies: ["Azure ML", "Kubeflow", "MLflow", "Kubernetes", "Python", "Airflow"],
        features: [
          "Procesy wdrażania modeli ML",
          "Magazyny cech i śledzenie eksperymentów",
          "Monitoring modeli i wykrywanie dryfu",
          "Testy A/B modeli w produkcji",
          "Infrastruktura GPU dla trenowania",
          "Automatyczne procesy ponownego trenowania"
        ]
      },
      {
        id: 3,
        title: "CI/CD i Automatyzacja",
        description: "Zaawansowane procesy CI/CD z automatycznym testowaniem, skanowaniem bezpieczeństwa i wdrożeniami wielośrodowiskowymi. GitOps i wydania bez przestojów.",
        technologies: ["Jenkins", "GitLab CI", "Azure DevOps", "GitHub Actions", "ArgoCD", "Helm"],
        features: [
          "Wieloetapowe procesy wdrożeniowe",
          "Automatyczne testowanie i bramki jakości",
          "Skanowanie bezpieczeństwa (SAST/DAST)",
          "GitOps z ArgoCD/Flux",
          "Wdrożenia bez przestojów",
          "Zarządzanie wydaniami i wycofywanie zmian"
        ]
      },
      {
        id: 4,
        title: "Integracja AI i Automatyzacja",
        description: "Wdrażanie rozwiązań AI w środowiskach korporacyjnych: GitHub Copilot, ChatGPT, Claude. Automatyzacja procesów programistycznych, przeglądu kodu i dokumentacji z wykorzystaniem AI.",
        technologies: ["GitHub Copilot", "OpenAI API", "Claude API", "Python", "LangChain", "Azure"],
        features: [
          "Wdrożenie GitHub Copilot w organizacji",
          "Integracja ChatGPT/Claude z procesami pracy",
          "Automatyzacja przeglądu kodu z AI",
          "Dokumentacja wspomagana przez AI",
          "Dedykowane procesy automatyzacji AI",
          "Bezpieczeństwo i zarządzanie AI"
        ]
      },
      {
        id: 5,
        title: "Site Reliability Engineering",
        description: "Praktyki SRE dla systemów korporacyjnych. Budżety błędów, reagowanie na incydenty, procesy dyżurów. Monitoring, alertowanie i analizy po incydentach.",
        technologies: ["Prometheus", "Grafana", "ELK Stack", "PagerDuty", "Azure Monitor", "Jaeger"],
        features: [
          "Definiowanie SLI/SLO i monitoring",
          "Reagowanie na incydenty i procesy dyżurów",
          "Rozproszone śledzenie i debugowanie",
          "Agregacja i analiza logów",
          "Podstawy inżynierii chaosu",
          "Analizy po incydentach i ciągłe doskonalenie"
        ]
      },
      {
        id: 6,
        title: "Aplikacje Web i Mobile",
        description: "Aplikacje React i React Native dla projektów wymagających pełnego stosu technologicznego. Frontend dla platform SaaS, panele administracyjne i aplikacje mobilne.",
        technologies: ["React", "React Native", "Next.js", "TypeScript", "Node.js", "PostgreSQL"],
        features: [
          "Aplikacje React/Next.js",
          "Aplikacje mobilne React Native",
          "Backend API Node.js/Python",
          "Projektowanie i optymalizacja baz danych",
          "Integracja z infrastrukturą chmurową",
          "DevOps dla aplikacji"
        ]
      }
    ],
    cta: {
      title: "Omówmy rozwiązanie!",
      subtitle: "Porozmawiajmy o Twoich wyzwaniach technicznych.",
      button: "Skontaktuj się",
      badge: "Zbudujmy Coś Razem"
    }
  },
  technologies: {
    title: "Chmura • DevOps • MLOps • Programowanie",
    subtitle: "Stos technologiczny klasy enterprise do budowania skalowalnej infrastruktury",
    categories: [
      {
        title: "Platformy Chmurowe",
        color: "#0078d4",
        technologies: [
          { name: "Microsoft Azure (Ekspert)", description: "AKS, Azure DevOps, Azure ML, Functions, Key Vault, Entra ID" },
          { name: "Amazon Web Services", description: "EKS, Lambda, SageMaker, S3, CloudFront, IAM" },
          { name: "Google Cloud Platform", description: "GKE, Cloud Functions, BigQuery - podstawowa znajomość" },
          { name: "Architektura Multi-cloud", description: "Projektowanie rozwiązań wielochmurowych, odtwarzanie awaryjne" }
        ]
      },
      {
        title: "Orkiestracja Kontenerów",
        color: "#326ce5",
        technologies: [
          { name: "Kubernetes", description: "AKS, EKS, RKE, wykresy Helm, autoskalowanie, polityki sieciowe" },
          { name: "Docker", description: "Wieloetapowe budowanie, skanowanie bezpieczeństwa, optymalizacja" },
          { name: "Helm", description: "Tworzenie wykresów, szablonowanie, zarządzanie wydaniami" },
          { name: "Service Mesh", description: "Istio, zarządzanie ruchem, polityki bezpieczeństwa" }
        ]
      },
      {
        title: "Infrastruktura jako Kod",
        color: "#7c3aed",
        technologies: [
          { name: "Terraform", description: "Dostawcy Azure/AWS/GCP, moduły, zarządzanie stanem" },
          { name: "Azure Bicep/ARM", description: "Natywne szablony Azure, modułowa architektura" },
          { name: "Ansible", description: "Zarządzanie konfiguracją, wdrażanie aplikacji" },
          { name: "Pulumi", description: "IaC w Python/TypeScript - podstawowa znajomość" }
        ]
      },
      {
        title: "CI/CD i Automatyzacja",
        color: "#ff6b35",
        technologies: [
          { name: "Azure DevOps", description: "Pipelines, Repos, Artifacts, Boards - pełny ALM" },
          { name: "Jenkins", description: "Pipeline jako kod, Job DSL, automatyzacja korporacyjna" },
          { name: "GitLab CI/CD", description: "Zintegrowany DevOps, skanowanie bezpieczeństwa, GitOps" },
          { name: "GitHub Actions", description: "Automatyzacja przepływów pracy, integracje z marketplace" },
          { name: "ArgoCD", description: "GitOps dla Kubernetes, deklaratywne wdrożenia" }
        ]
      },
      {
        title: "MLOps i Integracja AI",
        color: "#10a37f",
        technologies: [
          { name: "Azure Machine Learning", description: "Kompleksowa platforma ML, AutoML, zarządzane endpointy" },
          { name: "MLflow / Kubeflow", description: "Śledzenie eksperymentów, rejestr modeli, procesy ML" },
          { name: "GitHub Copilot", description: "Wdrożenia korporacyjne, zarządzanie, produktywność" },
          { name: "OpenAI / Claude API", description: "Integracja LLM w procesach biznesowych" }
        ]
      },
      {
        title: "Monitoring i SRE",
        color: "#ff6f00",
        technologies: [
          { name: "Prometheus i Grafana", description: "Własne metryki, panele, alertowanie, SLI/SLO" },
          { name: "Azure Monitor", description: "Log Analytics, Application Insights, alerty" },
          { name: "ELK Stack", description: "Elasticsearch, Logstash, Kibana - centralne logowanie" },
          { name: "Jaeger / Zipkin", description: "Rozproszone śledzenie, debugowanie wydajności" }
        ]
      },
      {
        title: "Programowanie i Backend",
        color: "#00ADD8",
        technologies: [
          { name: "Python", description: "Automatyzacja, skrypty, FastAPI, przetwarzanie danych" },
          { name: "Go (Golang)", description: "Wydajne narzędzia, aplikacje CLI" },
          { name: "Node.js / TypeScript", description: "Backend API, funkcje serverless" },
          { name: "React / React Native", description: "Aplikacje frontendowe, programowanie mobilne" }
        ]
      },
      {
        title: "Bezpieczeństwo i Zgodność",
        color: "#e53935",
        technologies: [
          { name: "Azure Key Vault / HashiCorp Vault", description: "Zarządzanie sekretami, szyfrowanie" },
          { name: "Entra ID (Azure AD)", description: "IAM, RBAC, dostęp warunkowy" },
          { name: "DevSecOps", description: "Checkov, skanowanie bezpieczeństwa, automatyzacja zgodności" },
          { name: "SOC2 / FAA Compliance", description: "Doświadczenie z audytami i certyfikacjami" }
        ]
      }
    ],
    cta: {
      badge: "Współpracujmy"
    }
  },
  about: {
    title: "Jakub Dudziński - Senior DevOps & MLOps Engineer",
    subtitle: "Architektura Chmury • MLOps • Kubernetes • Automatyzacja",
    description: "Senior DevOps & MLOps Engineer z 14+ lat doświadczenia w infrastrukturze chmurowej, automatyzacji i wdrażaniu rozwiązań klasy enterprise. Ekspert Azure, Kubernetes i Terraform. Projektuję skalowalne systemy, wdrażam modele ML w produkcji i automatyzuję procesy z wykorzystaniem AI. Od 2016 pracuję zdalnie z firmami z całego świata.",
    founderTitle: "Założyciel itj.dev",
    subtitles: {
      journey: "Od pierwszych projektów po architektury korporacyjne - oto moja ścieżka w DevOps i inżynierii chmurowej.",
      values: "Te wartości kierują każdym projektem infrastrukturalnym i każdym wdrożeniem."
    },
    sectionTitles: {
      journey: "Moja Droga",
      values: "Co Mną Kieruje",
      skills: "Kluczowe Kompetencje",
      cta: "Współpracujmy"
    },
    journey: [
      {
        period: "2012 - 2016",
        title: "Programowanie i Administracja Systemów",
        description: "Samodzielna nauka programowania (Go, PHP, Python). Administracja systemów Linux, pierwsze projekty webowe, budowanie fundamentów technicznych.",
        color: "#00ADD8"
      },
      {
        period: "2016 - 2020",
        title: "Programista Full-Stack i Praca Zdalna",
        description: "Start kariery komercyjnej i pracy zdalnej z zespołami międzynarodowymi. Aplikacje Laravel i React, projektowanie baz danych, pierwsze wdrożenia Docker i CI/CD.",
        color: "#7b68ee"
      },
      {
        period: "2020 - 2022",
        title: "DevOps Engineer - Lotnictwo i IoT",
        description: "Infrastruktura o krytycznym znaczeniu dla branży lotniczej (zgodność FAA), platformy IoT. Kubernetes, Terraform, architektura chmury hybrydowej.",
        color: "#326ce5"
      },
      {
        period: "2022 - 2024",
        title: "Senior DevOps Engineer i Team Lead",
        description: "Role seniorskie w firmach korporacyjnych. Migracje do chmury, architektura enterprise, prowadzenie zespołów DevOps. Redukcja kosztów o 60%, wdrożenia bez przestojów.",
        color: "#0078d4"
      },
      {
        period: "2024 - Obecnie",
        title: "Senior DevOps/MLOps Engineer - B2B",
        description: "Własna działalność i współpraca B2B. Infrastruktura dla e-commerce klasy enterprise, procesy MLOps, wdrażanie AI w środowiskach korporacyjnych. Przywództwo techniczne i rekrutacja.",
        color: "#4caf50"
      }
    ],
    values: [
      {
        title: "Automatyzacja Przede Wszystkim",
        description: "Jeśli coś można zautomatyzować - automatyzuję. Infrastruktura jako Kod, CI/CD, procesy MLOps. Zero manualnych procesów w produkcji.",
        icon: "engineering"
      },
      {
        title: "Rozwiązania Gotowe do Produkcji",
        description: "Nie tylko prototypy - buduję systemy gotowe na produkcję. Wysoka dostępność, odtwarzanie awaryjne, monitoring i bezpieczeństwo od pierwszego dnia.",
        icon: "code"
      },
      {
        title: "Architektura Świadoma Kosztów",
        description: "Optymalizacja kosztów chmury to część mojej pracy. FinOps, dobieranie zasobów, rezerwacje - infrastruktura która nie przepala budżetu.",
        icon: "leadership"
      },
      {
        title: "Kultura SRE",
        description: "Praktyki Site Reliability Engineering - budżety błędów, reagowanie na incydenty, analizy po awariach. Systemy które same się monitorują i alertują.",
        icon: "reliability"
      }
    ],
    skills: [
      { skill: "Azure i AWS Cloud" },
      { skill: "Kubernetes (AKS, EKS)" },
      { skill: "Terraform i IaC" },
      { skill: "CI/CD (Jenkins, GitLab, Azure DevOps)" },
      { skill: "MLOps i Azure ML" },
      { skill: "Monitoring (Prometheus, Grafana)" },
      { skill: "Docker i konteneryzacja" },
      { skill: "Integracja AI (Copilot, ChatGPT)" }
    ],
    cta: {
      title: "Potrzebujesz DevOps Engineer lub Cloud Architect?",
      subtitle: "Porozmawiajmy o Twojej infrastrukturze - od migracji do chmury po wdrożenie MLOps.",
      button: "Skontaktuj się"
    }
  },
  contact: {
    title: {
      line1: "Potrzebujesz",
      line2: "Infrastruktury?",
      line3: "Porozmawiajmy"
    },
    description: "Senior DevOps & MLOps Engineer z 14+ lat doświadczenia. Projektuję infrastrukturę chmurową, wdrażam procesy CI/CD i MLOps, integruję AI w środowiskach korporacyjnych. Azure, Kubernetes, Terraform.",
    info: {
      email_label: "Email",
      response_label: "Czas Odpowiedzi",
      email: BRAND.EMAIL,
      responseTime: "Odpowiedź w ciągu 12 godzin",
      location_label: "Lokalizacja",
      location: "Praca Zdalna (Klienci Globalni)"
    },
    social_title: "Połącz się",
    faq_title: "FAQ - DevOps i MLOps",
    faq_subtitle: `Pytania o infrastrukturę i automatyzację - ${BRAND.COMPANY_NAME}`,
    form: {
      nameLabel: "Imię *",
      emailLabel: "Email *",
      serviceLabel: "Chmura, DevOps czy MLOps?",
      messageLabel: "Opisz swoje wyzwanie",
      submitButton: "Wyślij Zapytanie",
      namePlaceholder: "Twoje imię",
      emailPlaceholder: "twoj@email.com",
      messagePlaceholder: "Opisz wyzwania infrastrukturalne - migracja do chmury, Kubernetes, CI/CD, MLOps, automatyzacja, monitoring..."
    },
    social: {
      github: BRAND.GITHUB_URL,
      linkedin: BRAND.LINKEDIN_URL
    },
    faq: [
      {
        question: "W jakich technologiach chmurowych się specjalizujesz?",
        answer: "Microsoft Azure to moja główna platforma - AKS, Azure DevOps, Azure ML, ARM/Bicep. Mam też doświadczenie z AWS (EKS, Lambda, SageMaker) i GCP. Architektura wielochmurowa to moja codzienność."
      },
      {
        question: "Czy możesz przeprowadzić migrację do chmury?",
        answer: "Tak, mam 15+ zrealizowanych migracji. Od prostego przeniesienia po pełną modernizację. Strategia migracji, ocena, wykonanie z minimalizacją przestojów, optymalizacja po migracji."
      },
      {
        question: "Co to jest MLOps i czy tego potrzebuję?",
        answer: "MLOps to DevOps dla Machine Learning - infrastruktura do trenowania, wdrażania i monitorowania modeli ML. Potrzebujesz tego jeśli masz modele ML i chcesz je wdrożyć w produkcji profesjonalnie."
      },
      {
        question: "Czy wdrażasz rozwiązania AI typu ChatGPT/Copilot?",
        answer: "Tak, specjalizuję się w integracji AI w środowiskach korporacyjnych: GitHub Copilot dla zespołów programistycznych, ChatGPT/Claude w procesach biznesowych, automatyzacja przeglądu kodu i dokumentacji."
      },
      {
        question: "Jaka jest forma współpracy?",
        answer: "Pracuję wyłącznie B2B jako kontraktor. Możliwe modele: dedykowany czas (np. 80-160h/mies.), projekt ze stałą ceną, stała współpraca dla wsparcia. Zawsze zaczynamy od rozmowy o potrzebach."
      },
      {
        question: "Czy prowadzisz zespoły DevOps?",
        answer: "Tak, mam doświadczenie w prowadzeniu zespołów 3-8 osób. Przywództwo techniczne, mentoring, rekrutacja techniczna, budowanie kultury DevOps/SRE. Mogę też wesprzeć istniejący zespół."
      },
      {
        question: "Jak wygląda współpraca przy projekcie?",
        answer: "1) Rozmowa wstępna (bezpłatna) 2) Ocena i propozycja 3) Iteracyjna realizacja z regularnymi pokazami 4) Dokumentacja i transfer wiedzy 5) Wsparcie po wdrożeniu. Komunikacja Slack/Teams."
      },
      {
        question: "Pracujesz zdalnie czy na miejscu?",
        answer: "Pracuję wyłącznie zdalnie - mam 10+ lat doświadczenia z firmami z Polski, UK, USA, Niemiec. Regularne spotkania wideo, asynchroniczna komunikacja, pełna transparentność."
      }
    ]
  },
  meta: {
    title: "itj.dev - Senior DevOps & MLOps Engineer | Azure, Kubernetes, Terraform, Integracja AI",
    description: "Senior DevOps & MLOps Engineer z 14+ lat doświadczenia. Infrastruktura chmurowa Azure/AWS, Kubernetes, Terraform, CI/CD. Procesy MLOps, wdrażanie AI w środowiskach korporacyjnych. Migracje do chmury, automatyzacja, monitoring. Freelancer DevOps z Polski.",
    keywords: "DevOps Engineer, MLOps Engineer, Azure DevOps, Kubernetes, AKS, Terraform, Infrastruktura jako Kod, CI/CD, Jenkins, GitLab CI, migracja do chmury, Azure Cloud, AWS, automatyzacja DevOps, monitoring Prometheus Grafana, Docker, Helm, SRE, Site Reliability Engineering, integracja AI, GitHub Copilot, freelancer DevOps Polska, kontraktor DevOps",
    ogTitle: "itj.dev - Senior DevOps & MLOps Engineer",
    ogDescription: "Infrastruktura chmurowa Azure/AWS, Kubernetes, Terraform. Procesy MLOps i wdrażanie AI. Migracje do chmury, CI/CD, automatyzacja. Freelancer DevOps z Polski.",
    twitterTitle: "itj.dev - DevOps & MLOps Engineer",
    twitterDescription: "Senior DevOps Engineer. Azure, Kubernetes, Terraform, CI/CD. MLOps, integracja AI. 14+ lat doświadczenia.",
    language: "Polish",
    locale: "pl_PL"
  }
};

// English Content
export const contentEN: ContentData = {
  navigation: {
    home: "Home",
    technologies: "Technologies",
    services: "Services",
    about: "About",
    contact: "Contact"
  },
  hero: {
    title: {
      line1: "Cloud.",
      line2: "Automate.",
      line3: "Scale."
    },
    description: "Senior DevOps & MLOps Engineer with 14+ years of experience. I design cloud infrastructure (Azure/AWS), deploy ML models in production, and automate everything possible. From Kubernetes to AI integration - delivering enterprise-grade solutions.",
    introduction: "Hi, I'm Jakub - specializing in DevOps & MLOps",
    tagline: "Cloud Infrastructure • MLOps & AI Integration • Kubernetes • CI/CD Automation",
    stats: {
      projects: { number: 40, label: "Infrastructure Projects", value: "40+" },
      years: { number: 14, label: "Years of Experience", value: "14+" },
      clients: { number: 15, label: "Cloud Migrations", value: "15+" },
      support: { text: "100+", label: "Automated Pipelines", value: "100+" }
    },
    technologies: {
      title: "Technologies",
      subtitle: "Tools I've mastered"
    },
    cta: {
      button: "Start Your Project",
      badge: "Ready to Scale?"
    },
    terminalTechnologies: [
      {
        name: "Go/Golang",
        command: "go run main.go",
        code: `package main\n\nimport "fmt"\n\nfunc main() {\n    fmt.Println("Hello, DevOps!")\n}`,
        output: "Hello, DevOps!",
        color: "from-blue-500 to-cyan-500"
      },
      {
        name: "Kubernetes",
        command: "kubectl apply -f deployment.yaml",
        code: `apiVersion: apps/v1\nkind: Deployment\nmetadata:\n  name: hello-app\nspec:\n  replicas: 3`,
        output: "deployment.apps/hello-app created\nservice/hello-app created",
        color: "from-purple-500 to-pink-500"
      },
      {
        name: "Docker",
        command: "docker run hello-world",
        code: `FROM node:18-alpine\nWORKDIR /app\nCOPY . .\nRUN npm install\nEXPOSE 3000\nCMD ["npm", "start"]`,
        output: "Hello from Docker!\nThis message shows that your installation appears to be working correctly.",
        color: "from-blue-600 to-indigo-600"
      },
      {
        name: "React",
        command: "npm start",
        code: `function App() {\n  return (\n    <div className="App">\n      <h1>Hello React!</h1>\n    </div>\n  );\n}`,
        output: "Compiled successfully!\nLocal: http://localhost:3000\nWebpack compiled with 0 errors",
        color: "from-cyan-500 to-blue-500"
      },
      {
        name: "Ansible",
        command: "ansible-playbook deploy.yml",
        code: `---\n- name: Deploy Application\n  hosts: webservers\n  tasks:\n    - name: Install nginx\n      apt:\n        name: nginx\n        state: present\n    - name: Start nginx\n      service:\n        name: nginx\n        state: started`,
        output: "PLAY [Deploy Application] ****\n\nTASK [Install nginx] ****\nok: [web1]\nok: [web2]\n\nTASK [Start nginx] ****\nchanged: [web1]\nchanged: [web2]\n\nPLAY RECAP ****\nweb1: ok=2 changed=1\nweb2: ok=2 changed=1",
        color: "from-red-500 to-orange-500"
      },
      {
        name: "Terraform",
        command: "terraform apply",
        code: `resource "aws_instance" "web" {\n  ami           = "ami-0c55b159cbfafe1d0"\n  instance_type = "t3.micro"\n  \n  tags = {\n    Name = "HelloWorld"\n  }\n}`,
        output: "Apply complete! Resources: 1 added, 0 changed, 0 destroyed.\nOutputs:\ninstance_ip = 54.123.45.67",
        color: "from-purple-600 to-pink-600"
      },
      {
        name: "PostgreSQL",
        command: "psql -c \"SELECT 'Hello Database!'\"",
        code: `CREATE TABLE users (\n  id SERIAL PRIMARY KEY,\n  name VARCHAR(100),\n  email VARCHAR(100)\n);\n\nINSERT INTO users (name, email) VALUES\n('DevOps Engineer', 'hello@itj.dev');`,
        output: "     ?column?\n------------------\n Hello Database!\n(1 row)\n\nINSERT 0 1",
        color: "from-blue-700 to-indigo-700"
      }
    ]
  },
  servicesPreview: {
    title: "DevOps • MLOps • Cloud Architecture",
    subtitle: "Enterprise-grade infrastructure and automation at every level",
    items: [
      {
        title: "Cloud & DevOps Infrastructure",
        description: "Azure/AWS, Kubernetes, Terraform, CI/CD - scalable production infrastructure with automation and monitoring",
        icon: "cloud"
      },
      {
        title: "MLOps & AI Integration",
        description: "Deploying ML models in production, ChatGPT/Claude/Copilot integration, AI-powered process automation",
        icon: "ai"
      },
      {
        title: "Web & Mobile Development",
        description: "React and React Native applications with modern stack - from MVP to enterprise solutions",
        icon: "mobile"
      }
    ],
    ctaText: "View Service Details",
    ctaUrl: "/en/services"
  },
  testimonials: {
    title: "Project References",
    subtitle: "Testimonials from technical managers on delivered system projects",
    items: [
      {
        id: 1,
        name: "Michael Johnson",
        position: "CTO",
        company: "FinTech Scale-up",
        content: "Jakub architected our complete platform - from Go backend to Kubernetes infrastructure. His ability to combine programming with DevOps expertise allowed us to handle 10x traffic without issues.",
        rating: 5
      },
      {
        id: 2,
        name: "Sarah Chen",
        position: "Engineering Manager",
        company: "E-commerce Enterprise",
        content: "Infrastructure modernization with cloud migration. Jakub led 6-person team, implemented CI/CD automation and reduced deployment time from weeks to minutes. Practical approach with concrete results.",
        rating: 5
      },
      {
        id: 3,
        name: "David Wilson",
        position: "Head of Platform",
        company: "International SaaS",
        content: "We needed expert who understands both code and infrastructure. Jakub architected Go microservices, implemented service mesh and designed monitoring. Team efficiency significantly increased.",
        rating: 5
      }
    ]
  },
  portfolio: {
    title: "Infrastructure & MLOps Projects",
    subtitle: "Enterprise-grade implementations: cloud migrations, Kubernetes, CI/CD automation and MLOps deployments",
    items: [
      {
        id: 1,
        title: "E-commerce Infrastructure - Azure + Kubernetes",
        description: "Complete infrastructure for international e-commerce platform. Azure migration, AKS clusters with auto-scaling, zero-downtime deployments. 60% cost reduction.",
        category: "Cloud + DevOps",
        technologies: ["Azure", "Kubernetes", "Terraform", "Helm", "Prometheus", "Grafana"],
        featured: true
      },
      {
        id: 2,
        title: "MLOps Pipeline - FinTech Platform",
        description: "MLOps platform for scoring system. Automated training, validation and ML model deployment. Feature store, experiment tracking, A/B testing in production.",
        category: "MLOps + DevOps",
        technologies: ["Azure ML", "Kubeflow", "MLflow", "Kubernetes", "Python", "Terraform"],
        featured: true
      },
      {
        id: 3,
        title: "Enterprise CI/CD - Code Quality Platform",
        description: "CI/CD pipeline architecture for code quality platform. Automated testing, security scanning, multi-environment deployments. ServiceNow integration.",
        category: "DevOps + Automation",
        technologies: ["Jenkins", "GitLab CI", "Azure DevOps", "Docker", "Kubernetes", "Checkov"],
        featured: true
      },
      {
        id: 4,
        title: "IoT Infrastructure - Kubernetes + Monitoring",
        description: "Distributed infrastructure for IoT platform handling millions of devices. On-premises Kubernetes, message queues, real-time monitoring and alerting.",
        category: "Infrastructure + SRE",
        technologies: ["Kubernetes", "NATS", "InfluxDB", "Prometheus", "Grafana", "Terraform"],
        featured: true
      },
      {
        id: 5,
        title: "Cloud Migration - Aviation Industry",
        description: "Mission-critical infrastructure migration for aviation industry (FAA compliance). Hybrid-cloud architecture, zero-downtime migration, disaster recovery.",
        category: "Cloud Migration",
        technologies: ["Azure", "AWS", "Terraform", "Ansible", "Docker", "Kubernetes"],
        featured: true
      },
      {
        id: 6,
        title: "AI Integration - Enterprise Automation",
        description: "GitHub Copilot and Claude AI deployment in development processes. Automated code review, documentation and testing. Integration with existing CI/CD.",
        category: "AI Integration",
        technologies: ["GitHub Copilot", "Claude API", "Python", "Azure DevOps", "Docker"],
        featured: false
      }
    ]
  },
  githubProjects: {
    title: "Open Source Contributions",
    subtitle: "Tools for DevOps and Development community - automation, backend libraries and deployment utilities",
    username: "truskul",
    featured: ["go-microservices-kit", "kubernetes-deployment-tools", "laravel-devops-helpers"]
  },
  services: {
    title: "DevOps • MLOps • Cloud Architecture",
    subtitle: "Enterprise-grade infrastructure, automation and AI deployment in production",
    items: [
      {
        id: 1,
        title: "Cloud Infrastructure & DevOps",
        description: "Designing and implementing cloud infrastructure on Azure/AWS. Kubernetes, Infrastructure as Code, monitoring and high availability. Cloud migrations with minimal downtime.",
        technologies: ["Azure", "AWS", "Kubernetes", "Terraform", "Helm", "Prometheus"],
        features: [
          "Cloud architecture Azure/AWS/GCP",
          "Production Kubernetes clusters (AKS, EKS)",
          "Infrastructure as Code with Terraform",
          "Monitoring, alerting and observability",
          "Disaster recovery and high availability",
          "Cost optimization and FinOps"
        ],
        highlighted: true
      },
      {
        id: 2,
        title: "MLOps & ML Infrastructure",
        description: "Infrastructure for Machine Learning workloads. MLOps pipelines, production model deployment, performance monitoring and automated retraining.",
        technologies: ["Azure ML", "Kubeflow", "MLflow", "Kubernetes", "Python", "Airflow"],
        features: [
          "ML model deployment pipelines",
          "Feature stores and experiment tracking",
          "Model monitoring and drift detection",
          "A/B testing models in production",
          "GPU infrastructure for training",
          "Automated retraining pipelines"
        ]
      },
      {
        id: 3,
        title: "CI/CD & Automation",
        description: "Advanced CI/CD pipelines with automated testing, security scanning and multi-environment deployments. GitOps and zero-downtime releases.",
        technologies: ["Jenkins", "GitLab CI", "Azure DevOps", "GitHub Actions", "ArgoCD", "Helm"],
        features: [
          "Multi-stage deployment pipelines",
          "Automated testing and quality gates",
          "Security scanning (SAST/DAST)",
          "GitOps with ArgoCD/Flux",
          "Zero-downtime deployments",
          "Release management and rollback"
        ]
      },
      {
        id: 4,
        title: "AI Integration & Automation",
        description: "Deploying AI solutions in enterprise: GitHub Copilot, ChatGPT, Claude. Automating development processes, code review and documentation with AI.",
        technologies: ["GitHub Copilot", "OpenAI API", "Claude API", "Python", "LangChain", "Azure"],
        features: [
          "GitHub Copilot enterprise deployment",
          "ChatGPT/Claude workflow integration",
          "AI-powered code review automation",
          "AI-assisted documentation",
          "Custom AI automation pipelines",
          "AI security and governance"
        ]
      },
      {
        id: 5,
        title: "Site Reliability Engineering",
        description: "SRE practices for enterprise systems. Error budgets, incident response, on-call processes. Monitoring, alerting and post-mortems.",
        technologies: ["Prometheus", "Grafana", "ELK Stack", "PagerDuty", "Azure Monitor", "Jaeger"],
        features: [
          "SLI/SLO definition and monitoring",
          "Incident response and on-call processes",
          "Distributed tracing and debugging",
          "Log aggregation and analysis",
          "Chaos engineering basics",
          "Post-mortems and continuous improvement"
        ]
      },
      {
        id: 6,
        title: "Web & Mobile Development",
        description: "React and React Native applications for projects requiring full stack. Frontend for SaaS platforms, dashboards and mobile applications.",
        technologies: ["React", "React Native", "Next.js", "TypeScript", "Node.js", "PostgreSQL"],
        features: [
          "React/Next.js applications",
          "React Native mobile apps",
          "Node.js/Python backend APIs",
          "Database design and optimization",
          "Cloud infrastructure integration",
          "DevOps for applications"
        ]
      }
    ],
    cta: {
      title: "Let's discuss solutions!",
      subtitle: "The greatest solutions start with a conversation.",
      button: "Get In Touch",
      badge: "Let's Build Something Great"
    }
  },
  technologies: {
    title: "Cloud • DevOps • MLOps • Development",
    subtitle: "Enterprise-grade stack for building scalable infrastructure",
    categories: [
      {
        title: "Cloud Platforms",
        color: "#0078d4",
        technologies: [
          { name: "Microsoft Azure (Expert)", description: "AKS, Azure DevOps, Azure ML, Functions, Key Vault, Entra ID" },
          { name: "Amazon Web Services", description: "EKS, Lambda, SageMaker, S3, CloudFront, IAM" },
          { name: "Google Cloud Platform", description: "GKE, Cloud Functions, BigQuery, basic familiarity" },
          { name: "Multi-cloud Architecture", description: "Cross-cloud solution design, disaster recovery" }
        ]
      },
      {
        title: "Container Orchestration",
        color: "#326ce5",
        technologies: [
          { name: "Kubernetes", description: "AKS, EKS, RKE, Helm charts, auto-scaling, network policies" },
          { name: "Docker", description: "Multi-stage builds, security scanning, optimization" },
          { name: "Helm", description: "Chart development, templating, release management" },
          { name: "Service Mesh", description: "Istio, traffic management, security policies" }
        ]
      },
      {
        title: "Infrastructure as Code",
        color: "#7c3aed",
        technologies: [
          { name: "Terraform", description: "Azure/AWS/GCP providers, modules, state management" },
          { name: "Azure Bicep/ARM", description: "Native Azure templates, modular architecture" },
          { name: "Ansible", description: "Configuration management, application deployment" },
          { name: "Pulumi", description: "IaC in Python/TypeScript, basic familiarity" }
        ]
      },
      {
        title: "CI/CD & Automation",
        color: "#ff6b35",
        technologies: [
          { name: "Azure DevOps", description: "Pipelines, Repos, Artifacts, Boards - full ALM" },
          { name: "Jenkins", description: "Pipeline as code, Job DSL, enterprise automation" },
          { name: "GitLab CI/CD", description: "Integrated DevOps, security scanning, GitOps" },
          { name: "GitHub Actions", description: "Workflow automation, marketplace integrations" },
          { name: "ArgoCD", description: "GitOps for Kubernetes, declarative deployments" }
        ]
      },
      {
        title: "MLOps & AI Integration",
        color: "#10a37f",
        technologies: [
          { name: "Azure Machine Learning", description: "End-to-end ML platform, AutoML, managed endpoints" },
          { name: "MLflow / Kubeflow", description: "Experiment tracking, model registry, ML pipelines" },
          { name: "GitHub Copilot", description: "Enterprise deployments, governance, productivity" },
          { name: "OpenAI / Claude API", description: "LLM integration in business processes" }
        ]
      },
      {
        title: "Monitoring & SRE",
        color: "#ff6f00",
        technologies: [
          { name: "Prometheus & Grafana", description: "Custom metrics, dashboards, alerting, SLI/SLO" },
          { name: "Azure Monitor", description: "Log Analytics, Application Insights, Alerts" },
          { name: "ELK Stack", description: "Elasticsearch, Logstash, Kibana - centralized logging" },
          { name: "Jaeger / Zipkin", description: "Distributed tracing, performance debugging" }
        ]
      },
      {
        title: "Development & Backend",
        color: "#00ADD8",
        technologies: [
          { name: "Python", description: "Automation, scripting, FastAPI, data processing" },
          { name: "Go (Golang)", description: "High-performance tools, CLI applications" },
          { name: "Node.js / TypeScript", description: "Backend APIs, serverless functions" },
          { name: "React / React Native", description: "Frontend apps, mobile development" }
        ]
      },
      {
        title: "Security & Compliance",
        color: "#e53935",
        technologies: [
          { name: "Azure Key Vault / HashiCorp Vault", description: "Secrets management, encryption" },
          { name: "Entra ID (Azure AD)", description: "IAM, RBAC, conditional access" },
          { name: "DevSecOps", description: "Checkov, security scanning, compliance automation" },
          { name: "SOC2 / FAA Compliance", description: "Experience with audits and certifications" }
        ]
      }
    ],
    cta: {
      badge: "Let's Work Together"
    }
  },
  about: {
    title: "Jakub Dudziński - Senior DevOps & MLOps Engineer",
    subtitle: "Cloud Architecture • MLOps • Kubernetes • Automation",
    description: "Senior DevOps & MLOps Engineer with 14+ years of experience in cloud infrastructure, automation and enterprise-grade solution delivery. Azure, Kubernetes and Terraform expert. I design scalable systems, deploy ML models in production and automate processes with AI. Since 2016 working remotely with companies worldwide.",
    founderTitle: "itj.dev Founder",
    subtitles: {
      journey: "From first projects to enterprise architectures - here's my path in DevOps and cloud engineering.",
      values: "These values guide every infrastructure project and every deployment."
    },
    sectionTitles: {
      journey: "My Journey",
      values: "What Drives Me",
      skills: "Core Competencies",
      cta: "Let's Work Together"
    },
    journey: [
      {
        period: "2012 - 2016",
        title: "Programming & System Administration",
        description: "Self-taught programming (Go, PHP, Python). Linux system administration, first web projects, building technical foundations.",
        color: "#00ADD8"
      },
      {
        period: "2016 - 2020",
        title: "Full-Stack Developer & Remote Work",
        description: "Started commercial career and remote work with international teams. Laravel and React applications, database design, first Docker and CI/CD deployments.",
        color: "#7b68ee"
      },
      {
        period: "2020 - 2022",
        title: "DevOps Engineer - Aviation & IoT",
        description: "Mission-critical infrastructure for aviation industry (FAA compliance), IoT platforms. Kubernetes, Terraform, hybrid-cloud architecture.",
        color: "#326ce5"
      },
      {
        period: "2022 - 2024",
        title: "Senior DevOps Engineer & Team Lead",
        description: "Senior roles in enterprise companies. Cloud migrations, enterprise architecture, leading DevOps teams. 60% cost reduction, zero-downtime deployments.",
        color: "#0078d4"
      },
      {
        period: "2024 - Present",
        title: "Senior DevOps/MLOps Engineer - B2B",
        description: "Own business and B2B collaboration. Enterprise e-commerce infrastructure, MLOps pipelines, AI deployment. Technical leadership and recruitment.",
        color: "#4caf50"
      }
    ],
    values: [
      {
        title: "Automation-First Mindset",
        description: "If something can be automated - I automate it. Infrastructure as Code, CI/CD, MLOps pipelines. Zero manual processes in production.",
        icon: "engineering"
      },
      {
        title: "Production-Grade Solutions",
        description: "Not just proof-of-concept - I build production-ready systems. High availability, disaster recovery, monitoring and security from day one.",
        icon: "code"
      },
      {
        title: "Cost-Conscious Architecture",
        description: "Cloud cost optimization is part of my work. FinOps, right-sizing, reserved instances - infrastructure that doesn't burn through budget.",
        icon: "leadership"
      },
      {
        title: "SRE Culture",
        description: "Site Reliability Engineering practices - error budgets, incident response, post-mortems. Systems that monitor and alert themselves.",
        icon: "reliability"
      }
    ],
    skills: [
      { skill: "Azure & AWS Cloud" },
      { skill: "Kubernetes (AKS, EKS)" },
      { skill: "Terraform & IaC" },
      { skill: "CI/CD (Jenkins, GitLab, Azure DevOps)" },
      { skill: "MLOps & Azure ML" },
      { skill: "Monitoring (Prometheus, Grafana)" },
      { skill: "Docker & Containerization" },
      { skill: "AI Integration (Copilot, ChatGPT)" }
    ],
    cta: {
      title: "Need a DevOps Engineer or Cloud Architect?",
      subtitle: "Let's discuss your infrastructure - from cloud migration to MLOps deployment.",
      button: "Get In Touch"
    }
  },
  contact: {
    title: {
      line1: "Need",
      line2: "Infrastructure?",
      line3: "Let's Talk"
    },
    description: "Senior DevOps & MLOps Engineer with 14+ years of experience. I design cloud infrastructure, implement CI/CD and MLOps pipelines, integrate AI in enterprise processes. Azure, Kubernetes, Terraform.",
    info: {
      email_label: "Email",
      response_label: "Response Time",
      location_label: "Location",
      email: BRAND.EMAIL,
      responseTime: "Response within 12 hours",
      location: "Remote Work (Global Clients)"
    },
    social_title: "Connect",
    faq_title: "FAQ - DevOps & MLOps",
    faq_subtitle: `Questions about infrastructure and automation - ${BRAND.COMPANY_NAME}`,
    form: {
      nameLabel: "Name *",
      emailLabel: "Email *",
      serviceLabel: "Cloud, DevOps or MLOps?",
      messageLabel: "Describe your challenge",
      submitButton: "Send Inquiry",
      namePlaceholder: "Your name",
      emailPlaceholder: "your@email.com",
      messagePlaceholder: "Describe your infrastructure challenges - cloud migration, Kubernetes, CI/CD, MLOps, automation, monitoring..."
    },
    social: {
      github: BRAND.GITHUB_URL,
      linkedin: BRAND.LINKEDIN_URL
    },
    faq: [
      {
        question: "What cloud technologies do you specialize in?",
        answer: "Microsoft Azure is my primary platform - AKS, Azure DevOps, Azure ML, ARM/Bicep. I also have experience with AWS (EKS, Lambda, SageMaker) and GCP. Multi-cloud architecture is my daily work."
      },
      {
        question: "Can you lead a cloud migration?",
        answer: "Yes, I have 15+ completed migrations. From lift-and-shift to full modernization. Migration strategy, assessment, execution with minimal downtime, post-migration optimization."
      },
      {
        question: "What is MLOps and do I need it?",
        answer: "MLOps is DevOps for Machine Learning - infrastructure for training, deploying and monitoring ML models. You need it if you have ML models and want to deploy them professionally in production."
      },
      {
        question: "Do you deploy AI solutions like ChatGPT/Copilot?",
        answer: "Yes, I specialize in enterprise AI integration: GitHub Copilot for development teams, ChatGPT/Claude in business processes, automated code review and documentation."
      },
      {
        question: "What type of cooperation do you offer?",
        answer: "I work exclusively B2B as a contractor. Possible models: dedicated time (e.g., 80-160h/month), fixed-price project, retainer for support. We always start with a needs discussion."
      },
      {
        question: "Do you lead DevOps teams?",
        answer: "Yes, I have experience leading teams of 3-8 people. Technical leadership, mentoring, technical recruitment, building DevOps/SRE culture. I can also support existing teams."
      },
      {
        question: "What does project collaboration look like?",
        answer: "1) Discovery call (free) 2) Assessment and proposal 3) Iterative delivery with regular demos 4) Documentation and knowledge transfer 5) Post-deployment support. Communication via Slack/Teams."
      },
      {
        question: "Do you work remotely or on-site?",
        answer: "I work exclusively remotely - 7+ years experience with companies from Poland, UK, USA, Germany. Regular video meetings, async communication, full transparency."
      }
    ]
  },
  meta: {
    title: "itj.dev - Senior DevOps & MLOps Engineer | Azure, Kubernetes, Terraform, AI Integration",
    description: "Senior DevOps & MLOps Engineer with 14+ years of experience. Cloud infrastructure Azure/AWS, Kubernetes, Terraform, CI/CD. MLOps pipelines, AI deployment in enterprise. Cloud migrations, automation, monitoring. Freelance DevOps from Poland.",
    keywords: "DevOps Engineer, MLOps Engineer, Azure DevOps, Kubernetes, AKS, Terraform, Infrastructure as Code, CI/CD, Jenkins, GitLab CI, Cloud Migration, Azure Cloud, AWS, cloud migration, DevOps automation, Prometheus Grafana monitoring, Docker, Helm, SRE, Site Reliability Engineering, AI integration, GitHub Copilot, freelance DevOps Poland, DevOps contractor",
    ogTitle: "itj.dev - Senior DevOps & MLOps Engineer",
    ogDescription: "Cloud infrastructure Azure/AWS, Kubernetes, Terraform. MLOps pipelines and AI deployment. Cloud migrations, CI/CD, automation. Freelance DevOps from Poland.",
    twitterTitle: "itj.dev - DevOps & MLOps Engineer",
    twitterDescription: "Senior DevOps Engineer. Azure, Kubernetes, Terraform, CI/CD. MLOps, AI integration. 14+ years of experience.",
    language: "English",
    locale: "en_US"
  }
};

// Content getter function
export const getContent = (lang: 'pl' | 'en' = 'pl'): ContentData => {
  return lang === 'en' ? contentEN : contentPL;
};